import React, { useState, useEffect } from 'react';
import { eventTypesAPI } from '../../services/api';

const EventTypesSettings = () => {
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEventType, setEditingEventType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    published: true,
    edit: 0
  });

  useEffect(() => {
    fetchEventTypes();
  }, []);

  const fetchEventTypes = async () => {
    try {
      const response = await eventTypesAPI.getAll();
      setEventTypes(response.data);
    } catch (err) {
      setError('Failed to fetch event types');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEventType) {
        await eventTypesAPI.update(editingEventType._id, formData);
      } else {
        await eventTypesAPI.create(formData);
      }
      fetchEventTypes();
      resetForm();
    } catch (err) {
      setError('Failed to save event type');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event type?')) {
      try {
        await eventTypesAPI.delete(id);
        fetchEventTypes();
      } catch (err) {
        setError('Failed to delete event type');
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', published: true, edit: 0 });
    setEditingEventType(null);
    setShowModal(false);
  };

  const handleEdit = (eventType) => {
    setFormData({
      name: eventType.name,
      published: eventType.published,
      edit: eventType.edit
    });
    setEditingEventType(eventType);
    setShowModal(true);
  };

  if (loading) return <div className="loading loading-spinner loading-lg"></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Event Types</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
        >
          Add Event Type
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Name</th>
              <th>Edit Value</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(eventTypes) && eventTypes.map((eventType) => (
              <tr key={eventType._id}>
                <td>{eventType.name}</td>
                <td>
                  <div className="badge badge-outline">
                    {eventType.edit}
                  </div>
                </td>
                <td>
                  <div className={`badge ${eventType.published ? 'badge-success' : 'badge-error'}`}>
                    {eventType.published ? 'Published' : 'Draft'}
                  </div>
                </td>
                <td>{new Date(eventType.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="space-x-2">
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleEdit(eventType)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-error"
                      onClick={() => handleDelete(eventType._id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              {editingEventType ? 'Edit Event Type' : 'Add New Event Type'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Name</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input input-bordered"
                  required
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Edit Value</span>
                </label>
                <input
                  type="number"
                  value={formData.edit}
                  onChange={(e) => setFormData({...formData, edit: parseInt(e.target.value) || 0})}
                  className="input input-bordered"
                  min="0"
                />
              </div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Published</span>
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    className="checkbox"
                  />
                </label>
              </div>
              <div className="modal-action">
                <button type="submit" className="btn btn-primary">
                  {editingEventType ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default EventTypesSettings;
